import { useTranslation } from "react-i18next";
import { Card } from "reactstrap";
import Drawer from "../../../../components/Drawer";
import NTable from "../../../../components/NTable";
import Actions from "../../../../components/NTable/Actions";
import { useNewTable } from "../../../../components/NTable/hook";
import { useAccess, useActionWithAccess } from "../../../../utils/hooks/access";
import BulkActions from "../../components/BulkActions";
import Topbar from "../../components/Topbar";
import { useWorkingShiftDelete } from "./Delete/useWorkingShiftDelete";
import { useDrawerProps } from "../../helpers/useDrawerProps";
import { useFetchResource } from "../../helpers/useFetchResource";
import { useImportModal } from "../../helpers/useImportModal";
import { useManageForm } from "../../helpers/useManageForm";
import { useOnExport } from "../../helpers/useOnExport";
import { useQueryFilters } from "../../helpers/useQueryFilters";
import { useVisibleColumnKeys } from "../../helpers/useVisibleColumnKeys";
import useSettingsCRUDHook from "../../hook";
import { useColumns } from "./columns";
import Form from "./Form";

function WorkingShift() {
	const { t } = useTranslation();

	const {
		state: { mode, data, totalItems, loading, submitting, error },
		selected,
		fetch,
		createSubmit,
		updateSubmit,
		goToHelp,
		goToView,
		goToCreate,
		goToEdit,
		goToClone,
		remove,
		batchRemove,
		settingsExport,
		loadingExport,
		setData,
	} = useSettingsCRUDHook("/working-shift");

	const { hasAccess: canAdd } = useAccess(
		"settings.general.workingShifts.canCreate",
	);

	const [openDeleteModal, deleteModals] = useWorkingShiftDelete({
		remove,
		batchRemove,
		permission: "settings.general.workingShifts.canDelete",
	});

	const { unarchiveModal, isFormOpen, formProps } = useManageForm({
		mode,
		selected,
		createSubmit,
		updateSubmit,
	});

	const columns = useColumns({
		edit: useActionWithAccess(
			goToEdit,
			"settings.general.workingShifts.canEdit",
		),
		remove: openDeleteModal,
		clone: useActionWithAccess(
			goToClone,
			"settings.general.workingShifts.canCreate",
		),
	});

	const table = useNewTable({
		data,
		columns,
		loading,
		totalItems,
		sessionFiltersKey: "workingShifts",
	});

	const queryFilters = useQueryFilters(table);

	const { open: drawerOpen, title: drawerTitle } = useDrawerProps({
		mode,
		help: t("help"),
		view: t("view-working-shift"),
		create: t("add-working-shift"),
		edit: t("edit-working-shift"),
		clone: t("clone-working-shift"),
	});

	useFetchResource({ fetch, state: table.getState() });

	const visibleColumnKeys = useVisibleColumnKeys(table);

	const onExport = useOnExport({
		url: "/export/working-shift",
		exportCall: settingsExport,
		queryFilters,
		visibleColumnKeys
	});

	const { open: openImportModal, modal: importModal } = useImportModal({
		importUrl: "/working-shift/import",
		exportUrl: "/export/working-shift",
		setData,
		exportCall: settingsExport,
		loading: loading || loadingExport,
	});

	return (
		<div className="d-flex h-100">
			<Card className="flex-fill mb-0">
				<Topbar
					title={t("working-shifts")}
					add={canAdd && goToCreate}
					help={goToHelp}
					// importFile={openImportModal}
					// onExport={onExport}
					loading={loading || loadingExport}
				/>

				<Card
					className="mb-auto"
					style={{ maxHeight: "calc(100% - 156px)" }}
				>
					<Actions
						filters={table.getState().columnFilters}
						setFilters={table.setColumnFilters}
						columns={table.getAllColumns()}
					>
						<BulkActions
							rows={table.getState().rowSelection}
							batchRemove={openDeleteModal}
						/>
					</Actions>

					<NTable table={table} />
				</Card>
			</Card>

			<Drawer open={drawerOpen} close={goToView} title={drawerTitle}>
				{isFormOpen ? (
					<Form
						{...formProps}
						error={error}
						loading={submitting}
						close={goToView}
					/>
				) : (
					<>{t("help")}</>
				)}
			</Drawer>

			{importModal}

			{deleteModals}

			{unarchiveModal}
		</div>
	);
}

export default WorkingShift;
